'use client';

import clsx from 'clsx/lite';
import { useRef, useState } from 'react';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Container } from '~/components/Container';
import { HeadlineSubheadCta } from '~/components/HeadlineSubheadCta';
import type {
  AboutPagePeopleSectionPeople,
  AcfCoachesAndStaff,
} from '~/types/graphql';
import { getMaskedPathSync } from '~/utils/url';

export function CoachesAndStaff({
  coachesAndStaff,
  isDark,
  team,
}: Omit<AcfCoachesAndStaff, 'isDynamic'> & {
  people?: Array<AboutPagePeopleSectionPeople>;
  isDark?: boolean;
  team?: string;
}) {
  if (!coachesAndStaff) return null;

  const { headline, subhead, cta, people } = coachesAndStaff;

  return (
    <div
      className={clsx(
        'px-[--site-margin] py-16',
        isDark && 'border-b border-current',
      )}
    >
      <Container className="gap-y-9 lg:gap-y-8">
        <HeadlineSubheadCta headline={headline} subhead={subhead} cta={cta} />

        <Swiper
          id="coaches-and-staff"
          className="w-full !pb-12"
          wrapperClass={clsx(
            '!h-auto',
            (people?.length ?? 0) < 4
              ? 'justify-start gap-x-8'
              : 'justify-between',
          )}
          slidesPerView={1}
          spaceBetween={32}
          modules={[Pagination]}
          pagination={{ clickable: true }}
          breakpoints={{
            768: {
              slidesPerView: 'auto',
              spaceBetween: 32,
              enabled: true,
            },
            1200: {
              slidesPerView: 4,
              spaceBetween: 0,
              enabled: (people?.length ?? 0) < 4,
            },
          }}
        >
          {!!people?.length &&
            people?.map((person, i) => (
              <SwiperSlide
                key={i}
                className="!w-[335px] md:max-w-[304px] lg:mb-8"
              >
                <PersonCard
                  className="w-full"
                  name={person?.name ?? ''}
                  role={person?.title ?? ''}
                  image={person?.headshot?.node?.sourceUrl ?? ''}
                  bio={person?.bio ? person.bio + person.bio + person.bio : ''}
                  team={team}
                />
              </SwiperSlide>
            ))}
          <style>{`
            #coaches-and-staff .swiper-pagination {
              position: static;
              display: flex;
              justify-content: center;
              margin-top: 36px;
              height: 10px;
            }
            #coaches-and-staff .swiper-pagination-bullet {
              background-color: #7a7a7a;
            }
            #coaches-and-staff .swiper-pagination-bullet-active {
              background-color: #000;
            }
            @media (min-width: 1200px) {
              #coaches-and-staff .swiper-wrapper {
                flex-wrap: wrap;
                ${(people?.length ?? 0) >= 4 ? 'transform: none !important;' : ''}
              }
              #coaches-and-staff .swiper-pagination {
                display: none;
              }
            }
          `}</style>
          {isDark && (
            <style>{`
            #coaches-and-staff .swiper-pagination-bullet {
              background-color: rgba(255, 255, 255, 0.8);
            }
            #coaches-and-staff .swiper-pagination-bullet-active {
              background-color: #FFFFF;
            }
          `}</style>
          )}
        </Swiper>
      </Container>
    </div>
  );
}

function PersonCard({
  name,
  role,
  image,
  bio,
  className,
  team,
}: {
  name?: string;
  role?: string;
  image?: string;
  bio?: string;
  className?: string;
  team?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const detailsRef = useRef<HTMLDivElement>(null);

  const openDetails = (e: React.MouseEvent<HTMLElement>) => {
    const details = detailsRef.current;
    // if e is details or child of details, do nothing
    if (e.target === details || details?.contains(e.target as Node)) {
      return;
    }
    setIsOpen(true);
  };

  const closeDetails = () => {
    setIsOpen(false);
  };

  return (
    <li
      className={clsx(
        'relative flex cursor-pointer flex-col items-center overflow-hidden rounded border border-black bg-white text-black',
        className,
      )}
      onClick={openDetails}
    >
      <img
        className="mx-auto aspect-[61/75] w-full object-cover object-top"
        src={getMaskedPathSync(image, team)}
        alt=""
      />
      <div className="flex h-[100px] w-full flex-col justify-center gap-y-3.5 border-t border-black">
        <div>
          <h3 className="text-center font-nova text-2xl font-bold leading-9">
            {name}
          </h3>
        </div>
        <div>
          <p className="m-0 -mb-[3px] -mt-[2px] text-center font-nova text-[15px] font-bold uppercase leading-none tracking-[1px]">
            {role}
          </p>
        </div>
      </div>

      <div
        ref={detailsRef}
        className={clsx(
          'details absolute inset-0 flex cursor-default flex-col items-start gap-6 bg-white p-6',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <button
          className="absolute right-0 top-0 flex h-[72px] w-[72px] items-center justify-center"
          onClick={closeDetails}
        >
          <svg className="h-6 w-6" viewBox="0 0 37 38" fill="none">
            <path d="M1 1.25L36.5 36.75" stroke="black"></path>
            <path d="M36.5 1.25L0.999998 36.75" stroke="black"></path>
          </svg>
        </button>
        <img
          className="aspect-square h-[146px] rounded-full object-cover object-top"
          src={getMaskedPathSync(image, team)}
          alt=""
        />
        <div className="flex flex-col gap-y-3.5 border-b border-black pb-6">
          <div>
            <h3 className="text-center font-nova text-2xl font-bold leading-9">
              {name}
            </h3>
          </div>
          <div>
            <p className="m-0 -mb-[3px] -mt-[2px] text-center font-nova text-[15px] font-bold uppercase leading-none tracking-[1px]">
              {role}
            </p>
          </div>
        </div>
        <div
          className="-mb-6 -mr-6 overflow-y-scroll pb-6 pr-6 font-nova text-[18px] leading-[24px] tracking-[-1.9%] scrollbar scrollbar-track-transparent scrollbar-thumb-[#7a7a7a]"
          style={{
            maskImage:
              'linear-gradient(to bottom, black calc(100% - 24px), transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to bottom, black calc(100% - 24px), transparent 100%)',
          }}
        >
          <p className="m-0">{bio}</p>
        </div>
      </div>
    </li>
  );
}
